// =============================================================================
// MeuDriver — Instructor Agenda (orchestrator)
// -----------------------------------------------------------------------------
// Cada bloco (grid semanal, editor de disponibilidade, exceções/cópia) foi
// extraído para `./agenda/`. Esta página orquestra dados e ações.
// =============================================================================

import { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react'
import { addWeeks, eachDayOfInterval, endOfWeek, format, parseISO, startOfWeek, subWeeks } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'sonner'

import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import type { AvailabilityExceptionRow, AvailabilityRow } from '@/types/database'

import {
  AgendaWeekGrid,
  type BookingWithStudent,
  type WeekAvailability,
} from './agenda/AgendaWeekGrid'
import { AgendaAvailabilityEditor } from './agenda/AgendaAvailabilityEditor'
import { AgendaExceptionsCard } from './agenda/AgendaExceptionsCard'

const WEEK_START_DAY = 1 as const

const defaultAvailability: WeekAvailability = Object.fromEntries(
  [0, 1, 2, 3, 4, 5, 6].map((d) => [d, { enabled: false, start_time: '08:00', end_time: '18:00' }]),
)

export default function InstructorAgenda() {
  const { user } = useAuth()

  const [instructorId, setInstructorId] = useState<string | null>(null)
  const [currentWeek, setCurrentWeek] = useState<Date>(new Date())
  const [bookings, setBookings] = useState<BookingWithStudent[]>([])
  const [availability, setAvailability] = useState<WeekAvailability>({ ...defaultAvailability })
  const [exceptions, setExceptions] = useState<AvailabilityExceptionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [savingAvailability, setSavingAvailability] = useState(false)
  const [savingException, setSavingException] = useState(false)
  const [copyingWeeks, setCopyingWeeks] = useState(false)

  // Resolve instructor id
  useEffect(() => {
    if (!user?.id) return
    supabase
      .from('instructors')
      .select('id')
      .eq('profile_id', user.id)
      .single()
      .then(({ data }) => setInstructorId(data?.id ?? null))
  }, [user?.id])

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: WEEK_START_DAY })
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: WEEK_START_DAY })
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd })

  const loadData = useCallback(async () => {
    if (!instructorId) return
    setLoading(true)
    try {
      const [bookingsRes, availRes, exceptRes] = await Promise.all([
        supabase
          .from('bookings')
          .select('*, student:profiles!bookings_student_id_fkey(full_name)')
          .eq('instructor_id', instructorId)
          .gte('scheduled_at', weekStart.toISOString())
          .lte('scheduled_at', weekEnd.toISOString())
          .in('status', ['confirmed', 'in_progress', 'completed']),
        supabase.from('availability').select('*').eq('instructor_id', instructorId),
        supabase
          .from('availability_exceptions')
          .select('*')
          .eq('instructor_id', instructorId)
          .order('date', { ascending: true }),
      ])

      const merged: WeekAvailability = { ...defaultAvailability }
      for (const row of (availRes.data ?? []) as AvailabilityRow[]) {
        merged[row.day_of_week] = {
          enabled: true,
          start_time: row.start_time.slice(0, 5),
          end_time: row.end_time.slice(0, 5),
        }
      }

      setBookings((bookingsRes.data ?? []) as BookingWithStudent[])
      setAvailability(merged)
      setExceptions((exceptRes.data ?? []) as AvailabilityExceptionRow[])
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorId, weekStart.toISOString()])

  useEffect(() => { loadData() }, [loadData])

  async function handleSaveAvailability() {
    if (!instructorId) return
    setSavingAvailability(true)
    try {
      await supabase.from('availability').delete().eq('instructor_id', instructorId)

      const inserts = Object.entries(availability)
        .filter(([, v]) => v.enabled)
        .map(([day, v]) => ({
          instructor_id: instructorId,
          day_of_week: Number(day),
          start_time: v.start_time + ':00',
          end_time: v.end_time + ':00',
        }))

      if (inserts.length > 0) {
        const { error } = await supabase.from('availability').insert(inserts)
        if (error) throw error
      }

      toast.success('Disponibilidade salva com sucesso!')
    } catch (err) {
      toast.error('Erro ao salvar disponibilidade.')
      if (import.meta.env.DEV) console.error(err)
    } finally {
      setSavingAvailability(false)
    }
  }

  async function handleAddException(date: string, startTime: string, endTime: string) {
    if (!instructorId || !date) return
    setSavingException(true)
    try {
      const { error } = await supabase.from('availability_exceptions').insert({
        instructor_id: instructorId,
        date,
        is_available: false,
        start_time: startTime ? startTime + ':00' : null,
        end_time: endTime ? endTime + ':00' : null,
      })
      if (error) throw error
      toast.success('Bloqueio adicionado!')
      loadData()
    } catch {
      toast.error('Erro ao adicionar bloqueio.')
    } finally {
      setSavingException(false)
    }
  }

  async function handleDeleteException(id: string) {
    const { error } = await supabase.from('availability_exceptions').delete().eq('id', id)
    if (error) {
      toast.error('Erro ao remover exceção.')
    } else {
      toast.success('Exceção removida.')
      setExceptions((prev) => prev.filter((e) => e.id !== id))
    }
  }

  async function handleCopyWeekExceptions(weeks: number) {
    if (!instructorId) return
    const currentWeekExceptions = exceptions.filter((e) => {
      const d = parseISO(e.date)
      return d >= weekStart && d <= weekEnd && !e.is_available
    })
    if (currentWeekExceptions.length === 0) {
      toast.info('Nenhum bloqueio nesta semana para copiar.')
      return
    }
    setCopyingWeeks(true)
    try {
      const inserts = []
      for (let w = 1; w <= weeks; w++) {
        for (const exc of currentWeekExceptions) {
          const origDate = parseISO(exc.date)
          const newDate = format(addWeeks(origDate, w), 'yyyy-MM-dd')
          inserts.push({
            instructor_id: instructorId,
            date: newDate,
            is_available: false,
            start_time: exc.start_time ?? null,
            end_time: exc.end_time ?? null,
          })
        }
      }
      const { error } = await supabase.from('availability_exceptions').insert(inserts)
      if (error) throw error
      toast.success(`Bloqueios copiados para ${weeks} semana(s)!`)
      loadData()
    } catch {
      toast.error('Erro ao copiar bloqueios.')
    } finally {
      setCopyingWeeks(false)
    }
  }

  const weekLabel = `${format(weekStart, "d 'de' MMM", { locale: ptBR })} – ${format(weekEnd, "d 'de' MMM yyyy", { locale: ptBR })}`

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Agenda</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCurrentWeek((w) => subWeeks(w, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentWeek(new Date())} className="gap-1.5">
            <RotateCcw className="h-3.5 w-3.5" />
            Esta semana
          </Button>
          <Button variant="outline" size="icon" onClick={() => setCurrentWeek((w) => addWeeks(w, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium text-gray-700 ml-1 hidden sm:block">{weekLabel}</span>
        </div>
      </div>
      <p className="text-sm text-muted-foreground sm:hidden">{weekLabel}</p>

      <div className="flex flex-col xl:flex-row gap-6">
        <div className="flex-1 min-w-0">
          <AgendaWeekGrid
            weekDays={weekDays}
            availability={availability}
            bookings={bookings}
            exceptions={exceptions}
            loading={loading}
          />
        </div>

        <div className="xl:w-80 space-y-4">
          <AgendaAvailabilityEditor
            availability={availability}
            saving={savingAvailability}
            onChange={setAvailability}
            onSave={handleSaveAvailability}
          />
          <AgendaExceptionsCard
            exceptions={exceptions}
            onAdd={handleAddException}
            onDelete={handleDeleteException}
            onCopyWeek={handleCopyWeekExceptions}
            saving={savingException}
            copying={copyingWeeks}
          />
        </div>
      </div>
    </div>
  )
}
